<template>
  <v-app-bar
    id="header"
    data-cy="header"
    app
    :shrink-on-scroll="false"
    :height="headerHeight"
    color="white"
    elevation="0"
  >
    <Container class="d-flex px-4 px-xl-0">
      <div class="d-flex align-center" data-cy="headerUlekareLogo">
        <Logo :height="logoMaxHeight" :is-clickable="false" />
      </div>
      <v-spacer data-cy="headerSpacer" />
      <div class="align-center justify-end d-none d-md-flex">
        <NavigationButton
          v-for="(item, index) in textButtons"
          :key="`text_${index}`"
          :item="item"
        />
        <div v-if="$domainRegion === 'cz'" class="holder__buttons__icon mr-4">
          <HamburgerIcon
            ref="hamburgerMenu"
            :lines="3"
            class="hamburger-menu"
            @click.native="toggleMenu"
          />
          <ul :class="['holder__submenu', { open: isMenuOpen }]">
            <li v-for="(link, index) in submenuLinks" :key="`submenu_${index}`">
              <component
                :is="link.to ? 'router-link' : 'a'"
                :href="link.href"
                :to="link.to"
                @click.native="closeMenu(link.text)"
              >
                {{ link.text }}
              </component>
            </li>
          </ul>
        </div>
        <v-btn small color="primary" :to="$localePath('login')">
          {{ $t('servicePage.header.login') }}
        </v-btn>
      </div>
      <v-btn icon class="pa-3 rounded-circle d-block d-md-none" @click="dialog = true">
        <v-img :src="$asset('img/icons/menu.svg')" />
      </v-btn>
    </Container>
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      content-class="header-menu-content"
      transition="dialog-bottom-transition"
    >
      <v-card class="pb-5 justify-start" elevation="0">
        <v-card-title class="py-4 px-2">
          <Logo :height="logoMaxHeight" :is-clickable="false" />
          <v-spacer />
          <CloseButton @click="dialog = false" />
        </v-card-title>
        <v-card-text class="d-flex flex-column">
          <template v-for="(item, index) in textButtons">
            <v-divider :key="`mobile-header-divider_${index}`" />
            <h3
              :key="`mobile-header-link_${index}`"
              class="font-weight-regular cursor-pointer pa-4"
              @click="mobileButtonClick(item)"
            >
              {{ item.text }}
            </h3>
          </template>
          <div
            v-if="$domainRegion === 'cz'"
            class="py-4 mt-5 mobile-menu__next d-flex flex-column pa-4"
          >
            <div
              class="d-flex justify-space-between align-center cursor-pointer"
              @click="toggleMobileSubmenu"
            >
              <h3 class="font-weight-regular cursor-pointer">
                {{ $t('servicePage.header.more') }}
              </h3>
              <div class="pa-2 grey-lighten-1 rounded-circle">
                <v-icon :class="{ 'rotate-180': isMobileSubmenuOpen }">mdi-chevron-down</v-icon>
              </div>
            </div>
            <ul :class="{ open: isMobileSubmenuOpen }">
              <li v-for="(link, index) in submenuLinks" :key="`mobile-submenu_${index}`">
                <component
                  :is="link.to ? 'router-link' : 'a'"
                  :href="link.href"
                  :to="link.to"
                  class="py-4"
                  @click.native.stop="
                    gtmPush({
                      event: 'button_click',
                      link_text: link.text,
                      link_location: 'header'
                    })
                  "
                >
                  <h5 class="font-weight-regular">{{ link.text }}</h5>
                </component>
              </li>
            </ul>
          </div>
          <div class="px-6 d-flex">
            <v-btn color="primary" :to="$localePath('login')" class="flex-grow-1">
              {{ $t('servicePage.header.login') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
export default {
  name: 'ServicePageHeader',
  data() {
    return {
      submenuItems: [
        ['servicePage.header.submenu.consultations', 'poradna-lekare'],
        ['servicePage.header.submenu.thematicCounselling', 'tematicke-poradny'],
        ['servicePage.header.submenu.topics', 'temata'],
        ['servicePage.header.submenu.diseases', 'nemoci-vysetreni'],
        ['servicePage.header.submenu.firstAid', 'prvni-pomoc'],
        ['servicePage.header.submenu.career', 'kariera'],
        ['servicePage.header.submenu.contact', { routeName: 'contactPage' }]
      ],
      headerHeight: 80,
      logoRightMaxWidth: 207,
      logoMaxHeight: 28,
      dialog: false,
      isMenuOpen: false,
      isMobileSubmenuOpen: false
    };
  },
  computed: {
    submenuLinks() {
      return this.submenuItems.map(linkDef => {
        const linkConfig = linkDef[1];

        if (typeof linkConfig === 'object' && linkConfig.routeName) {
          return {
            text: this.$t(linkDef[0]),
            to: this.$localePath(linkConfig.routeName)
          };
        }

        return {
          text: this.$t(linkDef[0]),
          href: `${import.meta.env.VITE_OLD_WEB_URL}/${linkConfig}`
        };
      });
    },
    textButtons() {
      const buttons = [
        {
          text: this.$t('servicePage.header.consultation'),
          target: 'servicePage',
          targetId: '#whatWeDo'
        },
        {
          text: this.$t('servicePage.header.forCompanies'),
          target: 'index'
        }
      ];

      if (this.$domainRegion === 'cz') {
        buttons.push({
          text: this.$t('servicePage.header.articles'),
          href: `${import.meta.env.VITE_OLD_WEB_URL}/clanek`
        });
      }

      buttons.push(
        ...[
          {
            text: this.$t('servicePage.header.references'),
            target: 'servicePage',
            targetId: '#stories'
          },
          {
            text: 'EN',
            buttonProps: {
              clickAction: () => {
                this.$router.push(this.$localePath({ name: 'registration', language: 'en' }));
              }
            }
          }
        ]
      );

      return buttons;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      const hamburger = this.$refs.hamburgerMenu;
      if (this.isMenuOpen) {
        hamburger.toCross();
      } else {
        hamburger.toHamburger();
      }
    },
    closeMenu(linkText) {
      if (!this.isMenuOpen) {
        return;
      }

      this.isMenuOpen = false;
      const hamburger = this.$refs.hamburgerMenu;
      hamburger.toHamburger();

      this.gtmPush({
        event: 'button_click',
        link_text: linkText,
        link_location: 'header'
      });
    },
    handleOutsideClick(event) {
      if (!this.isMenuOpen) {
        return;
      }

      const menuContainer = this.$el.querySelector('.holder__buttons__icon');
      if (menuContainer && !menuContainer.contains(event.target)) {
        this.closeMenu();
      }
    },
    mobileButtonClick(item) {
      if (item.buttonProps?.clickAction) {
        item.buttonProps.clickAction();
        this.dialog = false;
        return;
      }
      if (item.href) {
        window.location.href = item.href;
        this.dialog = false;
        return;
      }
      const currentPage = this.$router.history.current;
      const pageName = currentPage.name.split('___')[0];
      if (pageName === item.target) {
        this.$scrollTo(item.targetId, {
          offset: -110
        });
        this.gtmPush({
          event: 'button_click',
          link_location: `${item.targetId}`,
          link_text: `${item.text}`,
          'button.detail': 'menu'
        });
      } else {
        this.$router.push(
          this.$localePath({
            name: item.target,
            hash: item.targetId,
            query: this.$route.query
          })
        );
      }
      this.dialog = false;
    },
    toggleMobileSubmenu() {
      this.isMobileSubmenuOpen = !this.isMobileSubmenuOpen;
    }
  }
};
</script>

<style scoped lang="scss">
.header-submenu-link {
  &:hover {
    background-color: $grey;
    display: block;
  }
}

#header {
  border-bottom: $border-width solid $grey-lighten !important;
}

.holder__buttons__icon {
  cursor: pointer;
  border-radius: 50%;
  position: relative;
  display: block !important;
  padding: 0 1rem;

  .holder__submenu {
    opacity: 0;
    top: 40px;
    z-index: -100;
    pointer-events: none;
    position: absolute;
    background: #fff;
    border-radius: 15px;
    border: 1.5px solid #faf8f8;
    padding: 0.5rem;
    width: max-content;
    right: 0;
    transition:
      opacity 0.25s linear,
      top 0.25s linear,
      z-index 0.25s linear;

    &.open {
      top: 60px;
      z-index: 10002;
      opacity: 1;
      pointer-events: all;
    }
  }

  ul {
    list-style: none;

    li {
      padding: 0;
      position: relative;

      a {
        color: $black !important;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400 !important;
        padding: 0.75rem 1rem !important;
        border-radius: 0.75rem;
        width: 100%;
        display: inline-block;

        &:hover {
          background-color: $grey !important;
        }
      }

      + li {
        margin-top: 0.5em;
      }
    }
  }
}

.mobile-menu__next {
  ul {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    list-style: none;
    padding: 0;

    &.open {
      max-height: 600px;
    }

    li {
      &:first-child {
        padding-top: 1rem;
      }

      a {
        display: block;
        text-decoration: none;
        color: $black !important;

        &:hover {
          opacity: 0.8;
        }
      }
    }
  }
}

.v-icon {
  transition: transform 0.3s ease-in-out;
}

.rotate-180 {
  transform: rotate(180deg);
}

.grey-lighten-1 {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
